<template>
  <div class="col-xl-12">
    <div class="card" ref="callSummaryCard">
      <!-- HEADER -->
      <div
        class="card-header card-no-border"
        style="padding-bottom: 8px !important"
      >
        <div
          class="d-flex justify-content-between align-items-start flex-wrap gap-2"
        >
          <h5>CALL SUMMARY</h5>

          <div class="d-flex align-items-center gap-2">
            <div
              class="refresh-icon"
              @click="handleRefresh"
              :class="{ disabled: loading }"
              title="Refresh"
            >
              <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-sync'"></i>
            </div>

            <div class="icon-btn" @click="toggleFullScreen" title="Fullscreen">
              <i
                :class="isFullScreen ? 'fa fa-minimize' : 'fa fa-maximize'"
              ></i>
            </div>
          </div>
        </div>

        <div
          class="d-flex justify-content-start align-items-start flex-wrap gap-2"
        >
          <div class="header-top">
            <div class="d-flex gap-2 flex-wrap">
              <!-- Distributor -->
              <select
                v-model="selectedDistributor"
                class="form-select"
                @focus="lazyLoadDistributors"
                @change="fetchStats"
                :disabled="isDistributor || isDistributorBackOffice"
              >
                <option v-if="!isDistributor && !isDistributorBackOffice" value="">All Distributors</option>
                <option
                  v-for="d in distributors"
                  :key="d.profile_id"
                  :value="d.profile_id"
                >
                  {{ d.name }}
                </option>
              </select>

              <!-- Year -->
              <select
                v-model="selectedYear"
                class="form-select"
                @change="fetchStats"
              >
                <option v-for="y in yearOptions" :key="y" :value="y">
                  {{ y }}
                </option>
              </select>

              <!-- Month -->
              <select
                v-model="selectedMonth"
                class="form-select"
                @change="fetchStats"
              >
                <option v-for="(m, i) in months" :key="i" :value="i + 1">
                  {{ m }}
                </option>
              </select>

              <!-- Route -->
              <select
                v-model="selectedRoute"
                class="form-select sku-select"
                @focus="lazyLoadRoutes"
                @change="fetchStats"
              >
                <option value="">All Routes</option>
                <option v-for="r in routes" :key="r.id" :value="r.id">
                  {{ r.route_name }}
                </option>
              </select>

              <!-- SKU -->
              <!-- <select
                v-model="selectedSku"
                class="form-select sku-select"
                @focus="lazyLoadItems"
                @change="fetchStats"
              >
                <option value="">All SKUs</option>
                <option v-for="item in items" :key="item.id" :value="item.id">
                  {{ item.item_name }}
                </option>
              </select> -->
              <router-link
                :to="{
                  name: 'ProductiveVsNonProductiveDetail',
                  query: {
                    distributor: selectedDistributor,
                    year: selectedYear,
                    month: selectedMonth,
                    route: selectedRoute,
                  },
                }"
                target="_blank"
                class="btn btn-sm view-more-btn"
                title="View Details"
              >
                View Details
              </router-link>
            </div>
          </div>
        </div>

        <!-- <p class="current-month mb-0">
          {{ selectedYear }} {{ months[selectedMonth - 1] }}
        </p> -->
      </div>

      <!-- BODY -->
      <div class="card-body pt-0">
        <!-- LOADING -->
        <div v-if="loading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <!-- ERROR -->
        <div v-else-if="error" class="error-container">
          <p class="error-message">{{ error }}</p>
        </div>

        <!-- CHART -->
        <div v-else>
          <!-- Compact Call Summary Cards -->
          <div class="stat-cards-grid">
            <!-- First row: Scheduled -->
            <div class="summary-stat-card scheduled-card">
              <div class="stat-icon-wrapper">
                <i class="fa fa-calendar-check"></i>
              </div>

              <div class="stat-content">
                <span class="stat-label">Scheduled</span>
                <span class="stat-value">
                  {{ totalScheduled }}
                </span>
              </div>
            </div>

            <!-- First row: Value Per Call -->
            <div class="summary-stat-card value-card">
              <div class="stat-icon-wrapper">
                <i class="fa fa-coins"></i>
              </div>

              <div class="stat-content">
                <span class="stat-label">Value Per Call</span>

                <span
                  class="stat-value currency-value"
                  :title="formatCurrency(valuePerCall)"
                >
                  {{ formatCurrency(valuePerCall) }}
                </span>
              </div>
            </div>

            <!-- Second row: Productive -->
            <div class="summary-stat-card productive-card">
              <div class="stat-icon-wrapper">
                <i class="fa fa-circle-check"></i>
              </div>

              <div class="stat-content">
                <span class="stat-label">Productive</span>
                <span class="stat-value">
                  {{ totalProductive }}
                </span>
              </div>
            </div>

            <!-- Second row: Non-Productive -->
            <div class="summary-stat-card non-productive-card">
              <div class="stat-icon-wrapper">
                <i class="fa fa-circle-xmark"></i>
              </div>

              <div class="stat-content">
                <span class="stat-label">Non-Productive</span>
                <span class="stat-value">
                  {{ totalNonProductive }}
                </span>
              </div>
            </div>
          </div>

          <!-- Chart -->
          <div class="chart-container">
            <apexchart
              :key="chartKey"
              :height="chartHeight"
              type="line"
              :options="chartOptions"
              :series="series"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import {
  fetchDistributors,
  fetchCallSummaryStats,
  fetchItemNameList,
  fetchRouteList,
} from "@/services/dataService";

export default {
  components: {
    apexchart: VueApexCharts,
  },

  data() {
    return {
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      isDistributor: localStorage.getItem("user_label") === "DISTRIBUTOR",
      isDistributorBackOffice: localStorage.getItem("user_label") === "DISTRIBUTOR BACK OFFICE",

      loading: false,
      error: null,
      isFullScreen: false,
      chartHeight: 350,
      chartKey: 0,
      distributorsLoaded: false,
      itemsLoaded: false,
      routesLoaded: false,

      distributors: [],
      routes: [],
      items: [],

      selectedDistributor: "",
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      selectedRoute: "",
      selectedSku: "",

      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],

      valuePerCall: 0,
      rawData: [],
      series: [],
    };
  },

  // AFTER
  mounted() {
    if (this.isDistributor) {
      this.selectedDistributor = this.profileId;
      this.lazyLoadDistributors();
    } else if (this.isDistributorBackOffice) {
      this.selectedDistributor = this.reportingProfileId;
      this.lazyLoadDistributors();
    }
    this.fetchStats();
  },

  computed: {
    chartDayCount() {
      return 31;
    },

    xCategories() {
      return Array.from({ length: this.chartDayCount }, (_, i) => i + 1);
    },

    // chartDayCount() {
    //   return 30;
    // },

    // xCategories() {
    //   return Array.from({ length: this.chartDayCount }, (_, i) => i + 1);
    // },

    chartRawData() {
      return this.rawData.filter((item) => {
        const day = Number(item.day);
        return day >= 1 && day <= this.chartDayCount;
      });
    },

    yearOptions() {
      const years = [];
      for (let y = new Date().getFullYear(); y >= 2000; y--) {
        years.push(y);
      }
      return years;
    },

    chartOptions() {
      return {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: {
            enabled: false,
          },
          parentHeightOffset: 0,
        },

        stroke: {
          curve: "smooth",
          width: 2,
        },

        markers: {
          size: 2.6,
          strokeWidth: 1,
          hover: {
            size: 4.5,
          },
        },

        xaxis: {
          categories: this.xCategories,
          tickAmount: this.isFullScreen ? this.chartDayCount - 1 : undefined,

          title: {
            text: this.isFullScreen ? "" : "Day",
            offsetY: -4,
            style: {
              fontSize: "10px",
              fontWeight: 600,
              color: "#374151",
            },
          },

          labels: {
            show: true,
            rotate: 0,
            trim: false,
            hideOverlappingLabels: false,
            minHeight: this.isFullScreen ? 18 : 14,
            maxHeight: this.isFullScreen ? 20 : 16,
            offsetY: this.isFullScreen ? 0 : -1,

            formatter: (value) => {
              const day = Number(value);

              /*
        Fullscreen: show all dates.
        Normal card view: show selected dates only to avoid crowding.
      */
              if (this.isFullScreen) {
                return String(day);
              }

              if (day === 1 || day === 31 || day % 2 === 1) {
                return String(day);
              }

              return "";
            },

            style: {
              fontSize: this.isFullScreen ? "9px" : "8.5px",
              fontWeight: this.isFullScreen ? 650 : 650,
              colors: "#374151",
            },
          },

          axisTicks: {
            show: false,
          },

          axisBorder: {
            show: true,
            color: "#e5e7eb",
          },
        },

        yaxis: {
          title: {
            text: "Count",
            style: {
              fontSize: "10px",
              fontWeight: 500,
              color: "#374151",
            },
          },
          min: 0,
          labels: {
            style: {
              fontSize: "10px",
              fontWeight: 400,
              colors: "#4b5563",
            },
          },
        },

        legend: {
          position: "top",
          horizontalAlign: "center",
          fontSize: "10.5px",
          fontWeight: 500,
          markers: {
            width: 8,
            height: 8,
            radius: 999,
          },
          itemMargin: {
            horizontal: 7,
            vertical: 3,
          },
        },

        tooltip: {
          shared: true,
          intersect: false,
        },

        grid: {
          show: true,
          borderColor: "#e5e7eb",
          strokeDashArray: 0,
          padding: {
            top: 4,
            right: 10,
            bottom: this.isFullScreen ? 28 : 15,
            left: 6,
          },
        },
      };
    },

    // totalScheduled() {
    //   return this.rawData.reduce((s, d) => s + (d.scheduled_count || 0), 0);
    // },
    // totalProductive() {
    //   return this.rawData.reduce((s, d) => s + (d.productive_count || 0), 0);
    // },
    // totalNonProductive() {
    //   return this.rawData.reduce(
    //     (s, d) => s + (d.non_productive_count || 0),
    //     0,
    //   );
    // },

    totalScheduled() {
      return this.chartRawData.reduce(
        (s, d) => s + (Number(d.scheduled_count) || 0),
        0,
      );
    },

    totalProductive() {
      return this.chartRawData.reduce(
        (s, d) => s + (Number(d.productive_count) || 0),
        0,
      );
    },

    totalNonProductive() {
      return this.chartRawData.reduce(
        (s, d) => s + (Number(d.non_productive_count) || 0),
        0,
      );
    },
  },

  methods: {
    async lazyLoadDistributors() {
      if (this.distributorsLoaded) return;
      await this.fetchDistributorList();
      this.distributorsLoaded = true;
    },

    async lazyLoadItems() {
      if (this.itemsLoaded) return;
      await this.loadItems();
      this.itemsLoaded = true;
    },

    async lazyLoadRoutes() {
      if (this.routesLoaded) return;
      await this.loadRoutes();
      this.routesLoaded = true;
    },

    handleRefresh() {
      this.fetchStats();
    },
    formatCurrency(val) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "LKR",
        maximumFractionDigits: 2,
      }).format(val);
    },

    async fetchDistributorList() {
      try {
        const res = await fetchDistributors(this.profileId, this.token);
        this.distributors = res.data.data;
      } catch (err) {
        if (err.response?.status === 401) {
          return;
        }
        console.error(err);
      }
    },

    async loadItems() {
      try {
        const companyId = localStorage.getItem("company_id");
        const res = await fetchItemNameList(companyId, 1, this.token);
        this.items = res.data.data;
      } catch (err) {
        if (err.response?.status === 401) return;
        console.error(err);
      }
    },

    async loadRoutes() {
      try {
        const res = await fetchRouteList(this.profileId, this.token);
        this.routes = res.data.data;
      } catch (err) {
        if (err.response?.status === 401) return;
        console.error(err);
      }
    },

    async fetchStats() {
      try {
        this.loading = true;
        this.error = null;

        this.rawData = [];
        this.series = [];
        this.valuePerCall = 0;

        const payload = {
          /*
           * Always send the logged-in profile.
           * This is used for access-scope validation.
           */
          profile_id: Number(this.profileId),

          /*
           * Distributor selection is a separate filter.
           */
          distributor_profile_id: this.selectedDistributor
            ? Number(this.selectedDistributor)
            : "",

          year: Number(this.selectedYear),

          month: Number(this.selectedMonth),

          route_id: this.selectedRoute ? Number(this.selectedRoute) : "",

          sku_id: this.selectedSku ? Number(this.selectedSku) : "",
        };

        const response = await fetchCallSummaryStats(payload, this.token);

        const responseData = response?.data || {};

        if (responseData.success && Array.isArray(responseData.data)) {
          this.rawData = responseData.data;

          this.valuePerCall = Number(responseData.value_per_call) || 0;

          this.updateChartData();

          /*
           * Zero values are valid.
           * Only show an error when no daily rows exist.
           */
          if (this.rawData.length === 0) {
            this.error = responseData.message || "No data available";
          }

          return;
        }

        this.error = responseData.message || "No data available";
      } catch (error) {
        console.error("Call Summary API error:", error);

        this.error = error?.response?.data?.message || "Something went wrong";
      } finally {
        this.loading = false;
      }
    },

    // updateChartData() {
    //   const scheduled = Array(this.daysInMonth).fill(0);
    //   const productive = Array(this.daysInMonth).fill(0);
    //   const nonProductive = Array(this.daysInMonth).fill(0);

    //   this.rawData.forEach((d) => {
    //     const i = d.day - 1;
    //     if (i >= 0) {
    //       scheduled[i] = d.scheduled_count || 0;
    //       productive[i] = d.productive_count || 0;
    //       nonProductive[i] = d.non_productive_count || 0;
    //     }
    //   });

    //   // ✅ Compute monthly totals
    //   const totalScheduled = scheduled.reduce((a, b) => a + b, 0);
    //   const totalProductive = productive.reduce((a, b) => a + b, 0);
    //   const totalNonProductive = nonProductive.reduce((a, b) => a + b, 0);

    //   this.series = [
    //     { name: `Scheduled (${totalScheduled})`, data: scheduled },
    //     { name: `Productive (${totalProductive})`, data: productive },
    //     { name: `Non-Productive (${totalNonProductive})`, data: nonProductive },
    //   ];
    // },

    updateChartData() {
      const scheduled = Array(this.chartDayCount).fill(0);
      const productive = Array(this.chartDayCount).fill(0);
      const nonProductive = Array(this.chartDayCount).fill(0);

      this.chartRawData.forEach((d) => {
        const day = Number(d.day);
        const index = day - 1;

        if (index >= 0 && index < this.chartDayCount) {
          scheduled[index] = Number(d.scheduled_count) || 0;
          productive[index] = Number(d.productive_count) || 0;
          nonProductive[index] = Number(d.non_productive_count) || 0;
        }
      });

      const totalScheduled = scheduled.reduce((a, b) => a + b, 0);
      const totalProductive = productive.reduce((a, b) => a + b, 0);
      const totalNonProductive = nonProductive.reduce((a, b) => a + b, 0);

      this.series = [
        {
          name: `Scheduled (${totalScheduled})`,
          data: scheduled,
        },
        {
          name: `Productive (${totalProductive})`,
          data: productive,
        },
        {
          name: `Non-Productive (${totalNonProductive})`,
          data: nonProductive,
        },
      ];

      this.chartKey++;
    },

    setChartHeight(isFullScreen) {
      if (isFullScreen) {
        this.$nextTick(() => {
          const card = this.$refs.callSummaryCard;
          const cardHeader = card?.querySelector(".card-header");
          const statCards = card?.querySelector(".stat-cards-grid");

          const headerH = cardHeader?.offsetHeight || 0;
          const statH = statCards?.offsetHeight || 0;
          const padding = 48;

          const available = window.innerHeight - headerH - statH - padding;
          this.chartHeight = Math.max(250, available);
          this.chartKey++;
        });
      } else {
        this.chartHeight = 350;
      }
    },

    toggleFullScreen() {
      const el = this.$refs.callSummaryCard;

      if (!document.fullscreenElement) {
        el.requestFullscreen?.();
        this.isFullScreen = true;

        this.$nextTick(() => {
          this.setChartHeight(true);
          this.chartKey++;
        });
      } else {
        document.exitFullscreen?.();
        this.isFullScreen = false;
        this.chartHeight = 350;

        this.chartKey++;
      }
    },
  },
};
</script>

<style scoped>
/* .chart-container {
  width: 100%;
} */

.chart-container {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  padding: 0;
}

.chart-container::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.chart-container::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 999px;
}

.chart-container::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%);
  border-radius: 999px;
}

.chart-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
}

.chart-container::-webkit-scrollbar-corner {
  background: transparent;
}

.spinner-container {
  display: flex;
  justify-content: center;
  height: 150px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #6c5ce7;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.error-container {
  text-align: center;
}

.error-message {
  color: red;
  font-weight: bold;
}

.header-top {
  display: flex;
  flex-direction: column;
  align-items: end;
}

select {
  width: auto;
  font-size: 15px;
}

.sku-select {
  width: 130px !important;
}

select option {
  font-size: 15px;
}

.refresh-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
}

.refresh-icon:hover {
  background: #f1f3f5;
  color: #000;
}

.refresh-icon.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: 0.2s;
}

.icon-btn:hover {
  background: #f1f3f5;
  color: #000;
}

/* =========================================================
   COMPACT CALL SUMMARY CARDS
   ========================================================= */

.stat-cards-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  margin-bottom: 10px;
}

/*
 * First row:
 * Scheduled = 4 columns
 * Value Per Call = 8 columns
 */
.scheduled-card {
  grid-column: span 4;
}

.value-card {
  grid-column: span 8;
}

/*
 * Second row:
 * Productive = 6 columns
 * Non-Productive = 6 columns
 */
.productive-card,
.non-productive-card {
  grid-column: span 6;
}

.summary-stat-card {
  position: relative;

  display: flex;
  align-items: center;

  min-width: 0;
  min-height: 54px;

  gap: 9px;
  padding: 8px 11px;

  background: #ffffff;
  border: 1px solid #e5eaf0;
  border-radius: 9px;

  box-shadow:
    0 1px 3px rgba(15, 23, 42, 0.04),
    0 3px 8px rgba(15, 23, 42, 0.03);

  overflow: hidden;

  transition:
    transform 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease;
}

.summary-stat-card:hover {
  transform: translateY(-1px);
  border-color: #d4dce6;

  box-shadow:
    0 2px 5px rgba(15, 23, 42, 0.06),
    0 5px 12px rgba(15, 23, 42, 0.04);
}

/*
 * Thin left accent.
 */
.summary-stat-card::before {
  content: "";

  position: absolute;
  top: 8px;
  bottom: 8px;
  left: 0;

  width: 3px;
  border-radius: 0 3px 3px 0;
}

/*
 * Remove the large decorative circle.
 */
.summary-stat-card::after {
  display: none;
}

.stat-icon-wrapper {
  position: relative;
  z-index: 1;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;
  flex: 0 0 32px;

  border-radius: 8px;
  font-size: 13px;
}

.stat-content {
  position: relative;
  z-index: 1;

  display: flex;
  flex: 1;
  min-width: 0;

  flex-direction: column;
  justify-content: center;

  gap: 1px;
}

.stat-label {
  display: block;

  color: #64748b;
  font-size: 9.5px;
  font-weight: 700;
  line-height: 1.15;

  letter-spacing: 0.3px;
  text-transform: uppercase;

  /*
   * Stop labels breaking like:
   * SCHE / DULED
   */
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;

  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-value {
  display: block;

  color: #0f172a;
  font-size: 17px;
  font-weight: 750;
  line-height: 1.15;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.currency-value {
  /*
   * Value card is wider, so full currency normally fits.
   */
  font-size: 16px;
}

/* Scheduled */
.scheduled-card::before {
  background: #0984e3;
}

.scheduled-card .stat-icon-wrapper {
  color: #0984e3;
  background: rgba(9, 132, 227, 0.1);
}

.scheduled-card .stat-value {
  color: #0875ca;
}

/* Value Per Call */
.value-card::before {
  background: #6c5ce7;
}

.value-card .stat-icon-wrapper {
  color: #6c5ce7;
  background: rgba(108, 92, 231, 0.1);
}

.value-card .stat-value {
  color: #5b4dcc;
}

/* Productive */
.productive-card::before {
  background: #00b894;
}

.productive-card .stat-icon-wrapper {
  color: #00a985;
  background: rgba(0, 184, 148, 0.1);
}

.productive-card .stat-value {
  color: #009d7e;
}

/* Non-Productive */
.non-productive-card::before {
  background: #fd79a8;
}

.non-productive-card .stat-icon-wrapper {
  color: #e95f91;
  background: rgba(253, 121, 168, 0.11);
}

.non-productive-card .stat-value {
  color: #db4f82;
}

/*
 * Medium screens:
 * retain a proper 2 × 2 layout with equal widths.
 */
@media (max-width: 991px) {
  .scheduled-card,
  .value-card,
  .productive-card,
  .non-productive-card {
    grid-column: span 6;
  }

  .summary-stat-card {
    min-height: 52px;
  }
}

/*
 * Small mobile:
 * use one card per row only when there is not enough room.
 */
@media (max-width: 575px) {
  .stat-cards-grid {
    grid-template-columns: 1fr;
    gap: 7px;
    margin-bottom: 9px;
  }

  .scheduled-card,
  .value-card,
  .productive-card,
  .non-productive-card {
    grid-column: auto;
  }

  .summary-stat-card {
    min-height: 50px;
    padding: 7px 10px;
  }

  .stat-icon-wrapper {
    width: 30px;
    height: 30px;
    flex-basis: 30px;
    font-size: 12px;
  }

  .stat-label {
    font-size: 9px;
  }

  .stat-value {
    font-size: 16px;
  }

  .currency-value {
    font-size: 15px;
  }
}

/* Fullscreen remains compact */
.card:fullscreen .stat-cards-grid {
  gap: 10px;
}

.card:fullscreen .summary-stat-card {
  min-height: 58px;
  padding: 9px 13px;
}

.card:fullscreen .stat-value {
  font-size: 19px;
}

.card:fullscreen .currency-value {
  font-size: 18px;
}




.card,
.card-body {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.45) transparent;
}

.card::-webkit-scrollbar,
.card-body::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.card::-webkit-scrollbar-track,
.card-body::-webkit-scrollbar-track {
  background: transparent;
}

.card::-webkit-scrollbar-thumb,
.card-body::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%);
  border-radius: 999px;
}

.card::-webkit-scrollbar-thumb:hover,
.card-body::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
}

.card::-webkit-scrollbar-corner,
.card-body::-webkit-scrollbar-corner {
  background: transparent;
}

.chart-container :deep(.apexcharts-xaxis-label) {
  font-size: 8.5px !important;
  font-weight: 650 !important;
  fill: #374151 !important;
}

.chart-container :deep(.apexcharts-yaxis-label) {
  font-size: 10px !important;
  font-weight: 400 !important;
}

.chart-container :deep(.apexcharts-legend-text) {
  font-size: 10.5px !important;
  font-weight: 500 !important;
}

.card:fullscreen .chart-container :deep(.apexcharts-xaxis-label) {
  font-size: 9px !important;
  font-weight: 650 !important;
  fill: #374151 !important;
}

.card:fullscreen .chart-container :deep(.apexcharts-xaxis-title-text) {
  display: none !important;
}

.view-more-btn {
  background-color: #000000;
  border: 1px solid #000000;
  color: #ffffff;
  text-decoration: none;
}

.view-more-btn:hover,
.view-more-btn:focus {
  background-color: #111827;
  border-color: #111827;
  color: #ffffff;
}
</style>
